import request from '../request';
import reservations from '../../services/gma/reservations';
import {types} from '../../utils/js/types';
import {getSelectedExtrasFromReservation} from '../../utils/app/reservations';
import {isArrayNotEmpty} from '../../utils/js/array';

import {GMI, STATE} from '../../constants';

export const {EXTRAS} = STATE.UI.RESERVATIONS;

/**
 * @private
 * @returns {Object} {type: string, path: [], value: array}
 */
function clearResExtrasState() {
  return {type: GMI.SET_GMI_STATE, path: EXTRAS, value: []};
}

/**
 * @private
 * @param {Array} extras
 * @returns {Object} {type: string, path: [], value: array}
 */
function setResExtrasState(extras) {
  return {type: GMI.SET_GMI_STATE, path: EXTRAS, value: extras};
}

/**
 * @private
 * @desc Remove a single extra from extrasState
 * @param {String} code
 * @returns {Function}
 */
function clearExtraFromResExtrasState(code) {
  return (dispatch, getState) => {
    const extras = getState().getIn(EXTRAS).toJS();
    const extraIndex = extras.findIndex(extra => extra.code === code);

    if (extraIndex !== -1) extras.splice(extraIndex, 1);
    dispatch(setResExtrasState(extras));
  };
}

/**
 * @private
 * @desc Set a single extra in extrasState with new quantity
 * @param {Object} extra {code: string, name: string}
 * @param {Number} quantity
 * @returns {Function}
 */
function setResExtrasStateWithExtraAndQuantity({code, name}, quantity) {
  return (dispatch, getState) => {
    const extras = getState().getIn(EXTRAS).toJS();
    const extraIndex = extras.findIndex(extra => extra.code === code);

    extraIndex === -1 ?
      extras.push({code, name, quantity}) :
      extras[extraIndex].quantity = quantity;

    dispatch(setResExtrasState(extras));
  };
}

export const extrasState = {
  /**
   * @name set
   * @desc A global setter for all reservation.extras payload data
   * @param {String} [type]
   * @param {Object|Array} [args]
   * @returns {Function}
   * @memberof actions.reservations.extrasState
   * @example
   * // set one extra
   * store.dispatch(actions.reservations.extrasState.set('extra', {extra: {code: 'GPS', name: 'GPS Navigation Device'}, quantity: 1}));
   * // set all extras
   * store.dispatch(actions.reservations.extrasState.set(null, [{code: 'GPS', name: 'GPS Navigation Device', quantity: 1}]));
   */
  set(type = null, args = {}) {
    return dispatch => {
      if (types(type).isString) {
        if (type === 'extra') {
          dispatch(setResExtrasStateWithExtraAndQuantity(args.extra, args.quantity));
        }
      } else {
        dispatch(setResExtrasState(args));
      }
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for reservation.extras payload data
   * @param {String} [type]
   * @param {Object} [args]
   * @returns {Function}
   * @memberof actions.reservations.extrasState
   * @example
   * // clear one extra
   * store.dispatch(actions.reservations.extrasState.unset('extra', {code: 'GPS'}));
   * // clear all extras
   * store.dispatch(actions.reservations.extrasState.unset());
   */
  unset(type = null, args = {}) {
    return dispatch => {
      if (types(type).isString) {
        if (type === 'extra') {
          dispatch(clearExtraFromResExtrasState(args.code));
        }
      } else {
        dispatch(clearResExtrasState());
      }
    };
  }
};


/**
 * @name extrasUpdate
 * @desc Make the GMA reservations/extrasUpdate call with state data, optional cancel function
 * @param {Object} [opts] {cancelFn: function}
 * @param {Object} [data] {params}
 * @returns {Function}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.extrasUpdate());
 * store.dispatch(actions.reservations.extrasUpdate({cancelFn: c => cancel}));
 */
export function extrasUpdate(opts = {}, data = {}) {
  return (dispatch, getState) => {
    const extras = getState().getIn(EXTRAS).toJS()
      .map(({code, quantity}) => ({code, quantity}));
    Object.assign(data, {payload:{extras}});
    return dispatch(request({
      service: reservations.extrasUpdate,
      data,
      opts
    })).then(resp => {
      if (isArrayNotEmpty(resp.messages) && !resp.cancelled) {
        const reservation = getState().getIn(STATE.SESSION.concat(['gbo', 'reservation'])).toJS();
        const extras = getSelectedExtrasFromReservation({reservation});
        dispatch(extrasState.set(null, extras));
      }
      return resp;
    });
  };
}
